import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../lib/api';
import type { MessageTemplate, TemplateList, TemplateKind } from '../types';

/**
 * Вставка заготовки в поле ответа. Художник пишет одно и то же десятки раз —
 * шаблон подставляет текст с уже заполненными именем, номером и остатком, а
 * дальше он правит его как обычное сообщение.
 */
export function TemplatePicker({
  kind,
  orderId,
  applicationId,
  onPick,
}: {
  /** Какие заготовки показать первыми: по месту, откуда открыли форму. */
  kind?: TemplateKind;
  orderId?: string;
  applicationId?: string;
  onPick: (text: string) => void;
}) {
  const [open, setOpen] = useState(false);

  const params = new URLSearchParams();
  if (orderId) params.set('orderId', orderId);
  if (applicationId) params.set('applicationId', applicationId);

  const templates = useQuery({
    queryKey: ['templates', orderId ?? applicationId ?? 'plain'],
    queryFn: () => api<TemplateList>(`/templates?${params.toString()}`),
  });

  const items = sortForKind(templates.data?.items ?? [], kind);

  if (items.length === 0) {
    return null;
  }

  const pick = (template: MessageTemplate) => {
    onPick(template.rendered);
    setOpen(false);
    // Счётчик нужен только для порядка в списке, поэтому ответа не ждём.
    void api(`/templates/${template.id}/used`, { method: 'POST' }).catch(() => undefined);
  };

  return (
    <div className="template-picker">
      <button type="button" className="link-button" onClick={() => setOpen((value) => !value)}>
        {open ? 'Свернуть шаблоны' : 'Шаблоны'}
      </button>

      {open && (
        <div className="template-picker__list">
          {items.map((template) => (
            <button
              key={template.id}
              type="button"
              className="template-chip"
              onClick={() => pick(template)}
            >
              <strong>{template.title}</strong>
              <small>{template.rendered}</small>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Подходящие по месту — вверх, остальные следом: список один, порядок разный. */
function sortForKind(items: MessageTemplate[], kind?: TemplateKind): MessageTemplate[] {
  if (!kind) return items;

  return [...items].sort((left, right) => Number(right.kind === kind) - Number(left.kind === kind));
}
